import { useCallback, useEffect, useRef, useState } from 'react';
import { apiJson } from './api';
import { PROMO_BANNER } from '../content/site';

/**
 * Show-state client. Fetches the real `GET /event/state` (Phase 2); falls back
 * to a local derivation only if the API is unreachable so the page still renders.
 *
 * Contract (matches api/src/modules/event/event.service.ts):
 *   { phase, showStartAt, serverTime, watchUrl }
 *
 * The client computes a serverTime offset so the countdown can't drift with
 * a wrong local clock, and re-polls every 60s plus on tab focus.
 *
 * Dev/QA override: append ?phase=pre|live|post to the URL (persisted in
 * sessionStorage until cleared with ?phase=auto). The override wins over the
 * fetched phase so designers/QA can preview any state without an admin flip.
 */

export type EventPhase = 'PRE' | 'LIVE' | 'POST';

export type EventState = {
  phase: EventPhase;
  showStartAt: string; // ISO
  serverTime: string; // ISO
  /** The ONE admin-set watch destination for every phase (renamed from
   *  `replayUrl` 2026-08-17): Twitch button on LIVE/POST, WATCH NOW CTA, POST
   *  hero heading, docked bar, closed-entries gate. Null → callers fall back
   *  via watchUrlForPhase. See the API's EventState for the full contract. */
  watchUrl: string | null;
  /** HP promo banner gate (HX-43) — resolved server-side from the
   *  admin-configured window, so a wrong local clock can't surface it early.
   *  `bannerUrl` is null whenever the banner isn't live. */
  bannerVisible: boolean;
  bannerUrl: string | null;
};

/** Air date: August 20, 2026. Placeholder start hour (7 PM ET) until the
 * schedule is confirmed; Phase 2 moves this into event_config in the DB. */
export const SHOW_START_AT = '2026-08-20T23:00:00.000Z';

const PHASE_OVERRIDE_KEY = 'la:phaseOverride';

function readPhaseOverride(): EventPhase | null {
  const param = new URLSearchParams(window.location.search).get('phase');
  if (param) {
    const upper = param.toUpperCase();
    if (upper === 'PRE' || upper === 'LIVE' || upper === 'POST') {
      sessionStorage.setItem(PHASE_OVERRIDE_KEY, upper);
      return upper;
    }
    sessionStorage.removeItem(PHASE_OVERRIDE_KEY);
    return null;
  }
  const stored = sessionStorage.getItem(PHASE_OVERRIDE_KEY);
  return stored === 'PRE' || stored === 'LIVE' || stored === 'POST' ? stored : null;
}

const BANNER_OVERRIDE_KEY = 'la:bannerOverride';

/**
 * QA override for the promo banner, mirroring ?phase=: `?banner=on|off`
 * (clear with `?banner=auto`). Lets the banner be previewed and screenshotted
 * outside its scheduled window without moving the dates in the admin portal.
 * Display-only — the server still owns the real gate.
 */
function readBannerOverride(): boolean | null {
  const param = new URLSearchParams(window.location.search).get('banner');
  if (param) {
    const v = param.toLowerCase();
    if (v === 'on' || v === 'off') {
      sessionStorage.setItem(BANNER_OVERRIDE_KEY, v);
      return v === 'on';
    }
    sessionStorage.removeItem(BANNER_OVERRIDE_KEY);
    return null;
  }
  const stored = sessionStorage.getItem(BANNER_OVERRIDE_KEY);
  return stored === 'on' ? true : stored === 'off' ? false : null;
}

/** Applies the QA banner override to a state object, if one is set. */
function withBannerOverride(s: EventState): EventState {
  const o = readBannerOverride();
  if (o === null) return s;
  // Forcing the banner ON outside its window leaves bannerUrl null, so fall
  // back to the bundled URL rather than rendering a dead CTA.
  return { ...s, bannerVisible: o, bannerUrl: o ? (s.bannerUrl ?? PROMO_BANNER.href) : null };
}

const LAST_STATE_KEY = 'la:lastEventState:v2';

/**
 * Last server-confirmed state, cached per tab-session so warm navigations
 * render the correct phase on the FIRST paint instead of holding the page
 * below the hero for the /event/state round trip (see HomePage's
 * `phaseKnown` gate). Only real server responses are cached — never the
 * local fallback derivation. The QA ?phase= override is applied on read so
 * preview walks stay consistent across reloads.
 */
function readCachedState(): EventState | null {
  try {
    const raw = sessionStorage.getItem(LAST_STATE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as EventState;
    const validPhase = s.phase === 'PRE' || s.phase === 'LIVE' || s.phase === 'POST';
    if (!validPhase || typeof s.showStartAt !== 'string' || typeof s.serverTime !== 'string') {
      return null;
    }
    const override = readPhaseOverride();
    return withBannerOverride(override ? { ...s, phase: override } : s);
  } catch {
    return null;
  }
}

async function fetchEventState(): Promise<EventState> {
  const override = readPhaseOverride();
  try {
    const { data } = await apiJson<{ status: number; data: EventState }>('/event/state');
    try {
      sessionStorage.setItem(LAST_STATE_KEY, JSON.stringify(data));
    } catch {
      // Storage full/blocked — the cache is an optimization only.
    }
    // A QA ?phase= override wins over the server's phase (preview any state).
    return withBannerOverride(override ? { ...data, phase: override } : data);
  } catch {
    // API unreachable — derive locally so the page still renders.
    const now = new Date();
    const derived: EventPhase = now.toISOString() < SHOW_START_AT ? 'PRE' : 'LIVE';
    // withBannerOverride still applies here so ?banner= works with the API
    // down — which is exactly the local/dev case QA previews in.
    return withBannerOverride({
      phase: override ?? derived,
      showStartAt: SHOW_START_AT,
      serverTime: now.toISOString(),
      watchUrl: null,
      // The banner window lives server-side; with the API unreachable there is
      // no trustworthy answer, so the promo stays hidden rather than guessed.
      bannerVisible: false,
      bannerUrl: null,
    });
  }
}

const POLL_MS = 60_000;

export type EventStateView = EventState & {
  /** serverTime minus local clock at fetch — add to Date.now() for drift-corrected now. */
  serverOffsetMs: number;
};

/**
 * Returns `[state, refresh]` — `refresh` forces an immediate re-check outside
 * the 60s/focus poll (used by the /sweepstakes access gate to unlock the
 * instant the show goes live, and for its manual "Check Again" button).
 */
export function useEventState(): [EventStateView | null, () => void] {
  const [state, setState] = useState<EventStateView | null>(() => {
    const cached = readCachedState();
    // Offset 0 = trust the local clock until the fresh fetch corrects it.
    return cached ? { ...cached, serverOffsetMs: 0 } : null;
  });
  const timerRef = useRef<number | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      const s = await fetchEventState();
      setState({ ...s, serverOffsetMs: new Date(s.serverTime).getTime() - Date.now() });
    } catch {
      // Keep the last known state on transient failures; next poll retries.
    }
  }, []);

  useEffect(() => {
    void refresh();
    timerRef.current = window.setInterval(() => void refresh(), POLL_MS);
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return [state, refresh];
}
